import {
  forwardRef,
  type PropsWithChildren,
  useImperativeHandle,
  useRef,
} from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { useObserveContainerSize } from '../core/hooks/useObserveContainerSize.js';
import { ColorRule } from '../core/slots/ColorRule.js';
import { ChartType } from '../core/types/chart-types.js';
import type { ComponentWithName } from '../core/types/component-with-name.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';
import { GaugeChartLayout } from './components/GaugeChartLayout/GaugeChartLayout.js';
import {
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  DISPLAY_NAME,
  GAUGE_CHART_ARIA_LABEL,
} from './constants.js';
import { useGaugeChartConfig } from './hooks/useGaugeChartConfig.js';
import { GaugeChartProviders } from './providers/GaugeChartProviders.js';
import { Label } from './slots/Label.js';
import { ThresholdIndicator } from './slots/ThresholdIndicator.js';
import { GaugeChartToolbarSlot } from './slots/Toolbar.js';
import { Tooltip } from './slots/Tooltip.js';
import type { GaugeChartConsumerConfig } from './types/gauge-chart-config.js';
import type { GaugeChartProps, GaugeChartRef } from './types/gauge-chart.js';

const COMPONENT_NAME = 'GaugeChart';

const _GaugeChart = forwardRef<
  GaugeChartRef,
  PropsWithChildren<GaugeChartProps>
>((props, forwardedRef) => {
  const {
    value,
    width,
    height,
    style,
    className,
    prefixIcon,
    seriesActions,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId = 'gauge-chart-root',
    valueAccessor,
  } = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(props);

  const config = useGaugeChartConfig(props);
  const {
    min,
    max,
    color,
    formatter,
    thresholdsIndicators,
    colorRules,
    toolbar,
    intents,
    tooltip,
    valueAccessor: configValueAccessor,
    loading,
  } = config;

  const effectiveValueAccessor = valueAccessor ?? configValueAccessor;

  const chartWidth = coerceSizeValue(width) ?? DEFAULT_CHART_WIDTH;
  const chartHeight = coerceSizeValue(height) ?? DEFAULT_CHART_HEIGHT;

  const [{ height: containerHeight, width: containerWidth }, containerRef] =
    useObserveContainerSize();

  const gaugeChartRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => {
    const consumerConfig: GaugeChartConsumerConfig = {
      min,
      max,
      color,
      formatter,
      thresholdsIndicators,
      colorRules,
      toolbar,
      tooltip,
      valueAccessor: effectiveValueAccessor,
      loading: loading,
    };

    return {
      element: gaugeChartRef.current,
      getConfig: () =>
        JSON.stringify({ type: ChartType.GaugeChart, ...consumerConfig }),
    };
  });

  const ref = useMergeRefs<GaugeChartRef | HTMLDivElement>([
    forwardedRef,
    gaugeChartRef,
  ]);

  return (
    <div
      {...behavioralTrackingProps}
      ref={containerRef}
      style={{
        width: chartWidth,
        height: chartHeight,
        overflow: 'hidden',
        ...style,
      }}
      className={className}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-testid={dataTestId}
      role="img"
      aria-label={GAUGE_CHART_ARIA_LABEL}
      data-dt-component={COMPONENT_NAME}
    >
      <GaugeChartProviders
        config={config}
        width={containerWidth}
        height={containerHeight}
        value={value}
        valueAccessor={effectiveValueAccessor}
        explicitColor={color}
        seriesActions={seriesActions}
        loading={loading ?? false}
      >
        <GaugeChartLayout
          ref={ref}
          value={value}
          valueAccessor={effectiveValueAccessor}
          prefixIcon={prefixIcon}
          intents={intents}
          toolbar={toolbar}
        />
      </GaugeChartProviders>
    </div>
  );
});

(_GaugeChart as ComponentWithName<typeof _GaugeChart>).displayName =
  DISPLAY_NAME;

/**
 * A Gauge Chart is a circular chart that displays information on a numeric
 * scale.
 * It's great for showing performance metrics or progress towards a goal.
 *
 * @public
 */
export const GaugeChart = Object.assign(_GaugeChart, {
  ColorRule,
  Intent,
  Label,
  ThresholdIndicator,
  Toolbar: GaugeChartToolbarSlot,
  Tooltip,
});
